import h from 'hyperscript';
import { t } from 'javascripts/utils/withTranslation';

/*
 * Mark a form field as invalid and show an error message
 * @param {HTMLElement} $field - The form field to mark as invalid
 * @param {HTMLElement} [$formNode] - Optional - The form element containing the field
 * @param {HTMLElement} [$errorContainerNode] - Optional - The error container to show the error message in
 */
export const makeFieldInvalid = ($field, $formNode, $errorContainerNode) => {
  const id =
    $field.id ||
    $field.name ||
    `field-${Math.random().toString(36).substr(2, 9)}`;
  const $form = $formNode || $field.closest('form');
  const $errorContainer =
    $errorContainerNode || $form?.querySelector('.form__errors');
  const $label =
    $form?.querySelector(`label[for="${$field.id}"]`) ||
    $field.closest('.form-group')?.querySelector('label') ||
    $field.name;
  const labelText = $label?.textContent?.replace(/\*/g, '');
  const message = $field.validationMessage || t('Fehler');
  const $errorItem = h('li', [`${labelText}: ${message}`]);
  const $errorMessage = h(
    'p.form-group__error',
    { id: `${id}-error` },
    message,
  );

  $field.setAttribute('aria-invalid', 'true');
  $field.parentNode?.querySelector('.label')?.classList.add('label--invalid');
  const describeby = $field.getAttribute('aria-describedby') || '';
  $field.setAttribute('aria-describedby', `${describeby} ${id}-error`);

  // Remove any existing error message for this field
  const existingError = $field.parentNode.querySelector(`#${id}-error`);
  if (existingError) {
    existingError.remove();
  }
  if ($field.classList.contains('input--rte')) {
    $field.closest('.input__rte-wrapper')?.appendChild($errorMessage);
  } else {
    $field.insertAdjacentElement('afterend', $errorMessage);
  }
  $errorContainer.querySelector('ul').appendChild($errorItem);
};

/*
 * Clear invalid state from a form field
 * @param {HTMLElement} $field - The form field to clear invalid state from
 */
export const clearFieldInvalid = ($field) => {
  $field.removeAttribute('aria-invalid');
  $field.parentNode?.querySelector('.form-group__error')?.remove();
  $field.removeAttribute('aria-describedby');
  if ($field.classList.contains('input--rte')) {
    $field
      .closest('.input__rte-wrapper')
      ?.querySelector('.form-group__error')
      ?.remove();
  }
  $field.parentNode
    ?.querySelector('.label')
    ?.classList.remove('label--invalid');
};

/*
 * Register HTML5 form validation to collect and show all error messages
 * in a common error container when the form validation is triggered.
 * You can trigger this behaviour by calling the form's checkValidity() method or by submitting the form.
 * @param {HTMLFormElement} $form - The form element to register validation for
 * @param {HTMLElement} $submitButton - The submit button of the form (This can also be of type button, but then it must trigger the form submission or validation itself)
 * @example
 * // standard form with submit button, button type submit automatically triggers validation
 * document.querySelectorAll('form.form').forEach(($form) => {
    const $submit = $form.querySelector(
      'input[type=submit], button:not([type=button])',
    );
    registerHtml5Validation($form, $submit);
  });
 * // form with custom button triggering validation
   registerHtml5Validation($form, $trigger);
   const isFormValid = $form.checkValidity();
 */
export const registerHtml5Validation = ($form, $submitButton) => {
  let $errorContainer = $form.querySelector('.form__errors');

  if ($form.noValidate) {
    console.warn(
      'HTML5 validation skipped, because form has noValidate attribute set.',
    );
    return;
  }

  if (!$errorContainer) {
    $errorContainer = h(
      '.announcement.announcement--warning.form__errors',
      { ariaLive: 'polite' },
      h('.text', [h('p', h('strong', t('Achtung:'))), h('ul')]),
    );

    $form.prepend($errorContainer);
  }

  const inputHandler = (e) => {
    const $target = e.target;
    if ($target?.validity?.valid) {
      clearFieldInvalid($target);

      if ($target.tagName.toLowerCase() === 'select') {
        $target.removeEventListener('change', inputHandler);
      } else {
        $target.removeEventListener('input', inputHandler);
      }
    }
  };

  const showAllErrorMessages = ($formEl) => {
    $errorContainer = $errorContainer || $formEl.querySelector('.form__errors');
    if (!$errorContainer || !$errorContainer.querySelector('ul')) {
      console.warn(
        'No error container or the correct markup for it found in form',
      );
      return;
    }
    $errorContainer.querySelector('ul').innerHTML = '';
    $errorContainer.classList.remove('form__errors--active');
    $formEl.querySelectorAll('*[aria-invalid="true"]').forEach(($el) => {
      $el.removeAttribute('aria-invalid');
    });
    $formEl.querySelectorAll('.form-group__error').forEach(($el) => {
      $el.remove();
    });

    $formEl.querySelectorAll('.label--invalid').forEach(($el) => {
      $el.classList.remove('label--invalid');
    });

    const invalidFields = $form.querySelectorAll(':invalid');
    if (invalidFields.length === 0) {
      return;
    }
    $errorContainer.classList.add('form__errors--active');
    invalidFields.forEach(($field, i) => {
      makeFieldInvalid($field, $formEl, $errorContainer);

      if ($field.tagName.toLowerCase() === 'select') {
        $field.addEventListener('change', inputHandler);
      } else {
        $field.addEventListener('input', inputHandler);
      }
    });
    $errorContainer?.focus();
  };

  let errorShown = false;

  $form.addEventListener(
    'invalid',
    (e) => {
      if (!errorShown) {
        showAllErrorMessages($form);
        errorShown = true;
      }
    },
    true,
  );

  $form.addEventListener('submit', () => {
    errorShown = false;
  });

  $submitButton?.addEventListener('click', (e) => {
    errorShown = false;
    const $f = e.target.closest('form');
    const $err = $f.querySelector('.form__errors');
    $err?.classList.remove('form__errors--active');
    if ($err?.querySelector('ul')) {
      $err.querySelector('ul').innerHTML = '';
    }
  });
};

export default registerHtml5Validation;
